<template>
  <!-- 数据源页：远程五类（接口/字段映射/查询参数/全局切换/初始参数）
       + ★ v29.29 本地静态选项类（select/radio/checkbox/groupselect）的「选项列表」。
       ★ v29.32：原「保存/加载/复制/粘贴 配置」四按钮（后端 /sys/create_data_source_config +
       /sys/query_data_source_config_list，及 localStorage 剪贴板）已按用户要求整体删除。 -->
  <a-row :gutter="16">
    <!-- ═══ 远程五类：接口 + 字段映射 + 查询参数 + 全局切换 + 初始参数 ═══ -->
    <template v-if="isRemote">
      <a-col :span="24">
        <a-form-item label="数据源">
          <PostUrlField
            v-model:value="config.postUrl"
            :item-list="postUrlOptions"
            :loading="postUrlLoading"
            placeholder="请输入数据源"
            @select="onPostUrlSelect"
            @search="handlePostUrlSearch"
          />
        </a-form-item>
      </a-col>

      <SelectionModeConfig
        v-if="MODE_TYPES.includes(config.type)"
        :config="config"
        field="mode"
      />
      <SelectionModeConfig
        v-else-if="MULTIPLE_TYPES.includes(config.type)"
        :config="config"
        field="multiple"
      />
      <ShowSearchConfig :config="config" />

      <a-col :span="12">
        <a-form-item label="显示类型">
          <a-radio-group v-model:value="config.labelKeyType">
            <a-radio :value="1">单字段</a-radio>
            <a-radio :value="2">多字段</a-radio>
          </a-radio-group>
        </a-form-item>
      </a-col>
      <OptionKeysConfig
        remote
        :config="config"
        :options="paramsOptions"
        :loading="paramsOptionsLoading"
      />
      <a-col
        v-if="config.labelKeyType === 2"
        :span="12"
      >
        <a-form-item label="间隔符号">
          <a-input
            v-model:value="config.labelSpace"
            allow-clear
          />
        </a-form-item>
      </a-col>
      <a-col
        v-if="config.labelKeyType === 2"
        :span="24"
      >
        <a-form-item label="显示列表">
          <div
            v-for="(_, i) in config.labelKeyList || []"
            :key="i"
            class="row-line"
          >
            <span class="row-no">({{ i + 1 }})</span>
            <a-auto-complete
              v-model:value="config.labelKeyList[i]"
              :options="paramsOptions"
              :loading="paramsOptionsLoading"
              placeholder="请输入显示字段"
              :filter-option="filterOptionContains"
            />
            <a-button
              type="link"
              danger
              @click="config.labelKeyList.splice(i, 1)"
            >
              删除
            </a-button>
          </div>
          <a-button
            size="small"
            type="dashed"
            @click="addListKey('labelKeyList')"
          >
            + 添加显示字段
          </a-button>
        </a-form-item>
      </a-col>

      <a-col :span="24">
        <a-form-item label="扩展查询字段">
          <div
            v-for="(_, i) in config.extendedFields || []"
            :key="i"
            class="row-line"
          >
            <span class="row-no">({{ i + 1 }})</span>
            <a-auto-complete
              v-model:value="config.extendedFields[i]"
              :options="paramsOptions"
              :loading="paramsOptionsLoading"
              placeholder="请输入扩展查询字段"
              :filter-option="filterOptionContains"
            />
            <a-button
              type="link"
              danger
              @click="config.extendedFields.splice(i, 1)"
            >
              删除
            </a-button>
          </div>
          <a-button
            size="small"
            type="dashed"
            @click="addListKey('extendedFields')"
          >
            + 添加扩展查询字段
          </a-button>
        </a-form-item>
      </a-col>

      <a-col :span="24">
        <a-form-item label="默认值配置">
          <a-select
            v-model:value="config.defaultValueType"
            style="width: 200px"
            placeholder="未指定"
            :options="[
              { value: '', label: '未指定' },
              { value: 'first', label: '第一条数据' },
              { value: 'global', label: '全局值' },
            ]"
          />
          <template v-if="config.defaultValueType === 'global'">
            <a-input
              v-model:value="config.globalDefaultKey"
              placeholder="键"
              style="width: 200px; margin-left: 8px"
            />
            <a-input
              v-model:value="config.globalDefaultField"
              placeholder="字段名"
              style="width: 260px; margin-left: 8px"
            />
          </template>
        </a-form-item>
      </a-col>

      <QueryParamsConfig
        :config="config"
        :field-options="paramsOptions"
        :field-options-loading="paramsOptionsLoading"
      />

      <a-col :span="24">
        <div class="section-title">
          <span>全局切换配置</span>
          <a-button
            type="primary"
            size="small"
            @click="addRequiredCondition"
          >
            + 添加全局切换
          </a-button>
        </div>
        <div
          v-for="(item, ri) in config.required_condition || []"
          :key="ri"
          class="row-line"
        >
          <a-input
            v-model:value="item.key"
            placeholder="键"
            style="width: 260px"
          />
          <a-input
            v-model:value="item.field_name"
            placeholder="字段名"
            style="width: 360px"
          />
          <a-button
            type="link"
            danger
            @click="config.required_condition.splice(ri, 1)"
          >
            删除
          </a-button>
        </div>
      </a-col>

      <a-col :span="24">
        <a-form-item label="初始参数：JSON">
          <a-textarea
            v-model:value="initModelText"
            :rows="3"
            placeholder='{"is_active": true}'
          />
        </a-form-item>
      </a-col>
    </template>

    <!-- ═══ 本地静态选项类（select/radio/checkbox/groupselect）：选项列表
         （用独立 v-if 而非 v-else-if：与上面的远程分支靠类型互斥，不依赖相邻分支匹配） ═══ -->
    <OptionListConfig
      v-if="isLocalOption"
      :config="config"
      :record="record"
    />
  </a-row>
</template>

<script setup lang="ts">
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useUserStore } from 'liyu-pc-base/init-plugin';
import { debounce } from 'lodash';
import { computed, onMounted, ref, watch } from 'vue';
import { LOCAL_OPTION_TYPES, MODE_TYPES, MULTIPLE_TYPES, REMOTE_TYPES } from './constants';
import OptionKeysConfig from './OptionKeysConfig.vue';
import OptionListConfig from './OptionListConfig.vue';
import PostUrlField from './PostUrlField.vue';
import QueryParamsConfig from './QueryParamsConfig.vue';
import SelectionModeConfig from './SelectionModeConfig.vue';
import ShowSearchConfig from './ShowSearchConfig.vue';

/* 数据源配置页：postUrl 搜索（/sys/base_query_url_list）、选源自动回填、字段候选
  （/{app}/obtain_{model_name}_column_data）、labelKeyList/extendedFields、defaultValueType、
  查询参数（QueryParamsConfig）、required_condition、init_model。
  ★ v29.29：本地静态选项类（select/radio/checkbox/groupselect）也进本页——原版 FieldSetting 的
  「选项列表」就在「数据源」页；本页对它们只渲染选项列表，远程字段/接口那半块整体不渲染。 */
const props = defineProps<{
  config: any;
  /** 列配置行（透传 OptionListConfig：class_name 定值类型默认值、content_map 供导入） */
  record?: any;
}>();
const initModelText = defineModel<string>('initModelText', { default: '' });

const isRemote = computed(() => REMOTE_TYPES.includes(props.config?.type));
const isLocalOption = computed(() => LOCAL_OPTION_TYPES.includes(props.config?.type));

const userStore = useUserStore();
const current_org = (userStore as any).currentOrg;

/* ───────── 数据源搜索（/sys/base_query_url_list） ───────── */
const postUrlOptions = ref<any[]>([]);
const postUrlLoading = ref(false);
let postUrlTotal = 0;
/* ★ v29.22.2：响应序号守卫——debounce 只合并 500ms 内的输入，跨窗口的响应仍可能乱序
   （先发的请求后返回会把 itemList 反向覆盖成旧关键字子集 → 下拉空/越搜越少），只认最新。 */
let postUrlSeq = 0;

const getPostUrlData = debounce((obj: any) => {
  postUrlLoading.value = true;
  const seq = ++postUrlSeq;
  getApiData(obj, '/sys/base_query_url_list')
    .then((res: any) => {
      if (seq !== postUrlSeq) return; // 过期响应丢弃
      postUrlOptions.value = [];
      if (res && res.data && res.data.length > 0) {
        res.data.forEach((item: any) => {
          if (props.config.postUrl === item.post_url) {
            getParamsOptions(item.app, item.model_name);
          }
          postUrlOptions.value.push({ value: item.post_url, label: item.name, data: item });
        });
      }
      /* ★ v29.22.1：postUrlTotal 恒存「全量总数」——带 query_param 的远程过滤结果的 total
         是过滤后总数，不能覆盖（否则降到 ≤100 后后续输入不再触发远程搜索，列表永久收缩）。 */
      if (!obj.query_param) {
        postUrlTotal = res.total ?? postUrlOptions.value.length;
      }
      postUrlLoading.value = false;
    })
    .catch(() => {
      if (seq === postUrlSeq) {
        postUrlLoading.value = false;
      }
    });
}, 500);

onMounted(() => {
  /* ★ v29.29：本地选项类不拉数据源列表（/sys/base_query_url_list 只服务远程五类，白跑一次请求） */
  if (isRemote.value) {
    getPostUrlData({
      org_id: current_org?.id,
      is_tree: ['remotetreeselectpro', 'remotecascaderpro'].includes(props.config.type),
    });
  }
});

/* 远程类型互切时本组件不重挂载，按新类型（是否树形）重拉数据源列表（对齐原版 onTypeChange 行为） */
watch(
  () => props.config.type,
  t => {
    if (t && REMOTE_TYPES.includes(t)) {
      getPostUrlData({
        org_id: current_org?.id,
        is_tree: ['remotetreeselectpro', 'remotecascaderpro'].includes(t),
      });
    }
  },
);

const handlePostUrlSearch = (str: string) => {
  const is_tree = ['remotetreeselectpro', 'remotecascaderpro'].includes(props.config.type);
  /* ★ v29.22.1：清空输入 → 重拉全量列表（此前清空不恢复，itemList 停在旧关键字的远程子集） */
  if (!str || !str.trim()) {
    getPostUrlData({ org_id: current_org?.id, is_tree });
    return;
  }
  if (postUrlTotal > 100) {
    getPostUrlData({ org_id: current_org?.id, is_tree, query_param: str });
  }
};

/* 选中数据源 → 自动回填（port 自 FieldSetting.selectPostUrl；item = {value, label, data}） */
const onPostUrlSelect = (item: any) => {
  const data = item?.data;
  if (!data) return;
  const parameters = data.parameters;
  if (parameters) {
    props.config.labelKeyType = 1;
    props.config.labelKey = parameters.label_key;
    props.config.valueKey = parameters.value_key;
    props.config.queryType = 'simple';
    props.config.simpleSearchConditions = [];
    (parameters.search_fields || []).forEach((key: any) => {
      props.config.simpleSearchConditions.push({
        searchType: 1,
        field_name: key,
        select_name: key,
      });
    });
  }
  getParamsOptions(data.app, data.model_name);
};

/* 字段候选（/{app}/obtain_{model_name}_column_data，扁平化带前缀） */
const paramsOptions = ref<any[]>([]);
const paramsOptionsLoading = ref(false);
const processParamsOptions = (res: any[], out: any[], prefix = '') => {
  prefix = prefix.replace('ID', '');
  if (prefix) prefix = `${prefix}-`;
  for (const item of res) {
    out.push({ value: item.name, label: `${prefix}${item.verbose_name}` });
    if (item.children) processParamsOptions(item.children, out, `${prefix}${item.verbose_name}`);
  }
};
const getParamsOptions = async (app: string, model_name: string) => {
  if (app && model_name) {
    paramsOptionsLoading.value = true;
    try {
      const res = await getApiData({}, `/${app}/obtain_${model_name}_column_data`, false, {
        skip_error: true,
      });
      if (!res) {
        paramsOptions.value = [];
      } else {
        const out: any[] = [];
        processParamsOptions(res, out);
        paramsOptions.value = out;
      }
    } finally {
      paramsOptionsLoading.value = false;
    }
  }
};

const filterOptionContains = (input: string, option: any) =>
  !input || (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

/* ───────── 列表键增删（labelKeyList / extendedFields） ───────── */
const addListKey = (key: 'labelKeyList' | 'extendedFields') => {
  if (!Array.isArray(props.config[key])) props.config[key] = [];
  props.config[key].push('');
};

/* ───────── 全局切换配置 ───────── */
const addRequiredCondition = () => {
  if (!Array.isArray(props.config.required_condition)) props.config.required_condition = [];
  props.config.required_condition.push({ key: '', field_name: '' });
};

/* ★ v29.32：原「数据源配置 保存 / 加载 / 复制 / 粘贴」整块（CLIP_KEY 剪贴板、saveModal、loadModal、
   pickDsConfig 及 6 个 handle* 函数，依赖 /sys/create_data_source_config 与
   /sys/query_data_source_config_list）已按用户要求删除。 */
</script>

<style scoped lang="less">
.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  margin: 8px 0;
  padding-top: 8px;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}
.row-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.row-no {
  color: rgba(0, 0, 0, 0.45);
  flex: none;
  width: 28px;
}
</style>
